import { TemplateHelperMetadata } from '../types';

const STORAGE_PREFIX = 'template-helpers-metadata';

export const TEMPLATE_HELPERS_CACHE_VERSION = 'v1';
export const TEMPLATE_HELPERS_DEFAULT_TTL_MS = 30 * 60 * 1000;

interface TemplateHelpersCachePayload {
  version: string;
  fetchedAt: number;
  ttlMs: number;
  data: TemplateHelperMetadata[];
}

interface GetTemplateHelpersStorageOptions {
  scopeKey: string;
  version: string;
  allowStale?: boolean;
}

export const getDefaultTemplateHelpersCacheScope = (): string => {
  if (typeof window === 'undefined') {
    return 'ssr';
  }
  return window.location.origin || 'unknown-origin';
};

export const buildTemplateHelpersStorageKey = (scopeKey: string): string => {
  return `${STORAGE_PREFIX}:${scopeKey}`;
};

export const saveTemplateHelpersToSessionStorage = (
  data: TemplateHelperMetadata[],
  scopeKey: string,
  version = TEMPLATE_HELPERS_CACHE_VERSION,
  ttlMs = TEMPLATE_HELPERS_DEFAULT_TTL_MS,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const payload: TemplateHelpersCachePayload = {
      version,
      fetchedAt: Date.now(),
      ttlMs,
      data,
    };
    sessionStorage.setItem(buildTemplateHelpersStorageKey(scopeKey), JSON.stringify(payload));
  } catch {
    // Ignore sessionStorage failures and keep runtime flow in-memory.
  }
};

export const getTemplateHelpersFromSessionStorage = (
  options: GetTemplateHelpersStorageOptions,
): TemplateHelperMetadata[] | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const { scopeKey, version, allowStale = false } = options;
  try {
    const rawValue = sessionStorage.getItem(buildTemplateHelpersStorageKey(scopeKey));
    if (!rawValue) {
      return null;
    }

    const payload = JSON.parse(rawValue) as TemplateHelpersCachePayload;
    if (!payload || !Array.isArray(payload.data)) {
      return null;
    }

    if (payload.version !== version) {
      return null;
    }

    const isExpired = Date.now() - payload.fetchedAt > payload.ttlMs;
    if (isExpired && !allowStale) {
      return null;
    }

    return payload.data;
  } catch {
    return null;
  }
};

export const clearTemplateHelpersSessionStorage = (scopeKey?: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (scopeKey) {
      sessionStorage.removeItem(buildTemplateHelpersStorageKey(scopeKey));
      return;
    }

    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(`${STORAGE_PREFIX}:`)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Ignore sessionStorage failures and keep runtime flow in-memory.
  }
};
